// API Types
export interface City {
  id: number;
  name: string;
  region: string | null;
  country_code: string;
  cdek_city_id: number;
  display: string;
}

// Категории доставки для UI badges
export type DeliveryCategory = 'greenwax' | 'courier' | 'pickup' | 'post';

// Провайдеры доставки
export type DeliveryProvider = 'cdek' | 'yandex' | 'greenwax' | 'boxberry' | 'post';

export interface DeliveryOption {
  id: string;
  provider: DeliveryProvider;
  type: 'pickup' | 'courier' | 'express';
  category: DeliveryCategory;
  name: string;
  price: number;
  original_price: number;
  markup: number;
  min_days: number;
  max_days: number;
  is_free: boolean;
  pickup_points_count?: number;
  logo_url?: string;
}

export interface CourierAddress {
  fullName: string;
  street: string;
  house: string;
  apartment?: string;
  entrance?: string;
  floor?: string;
  comment?: string;
}

export interface AddressSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    street?: string;
    house?: string;
    geo_lat?: string;
    geo_lon?: string;
  };
}

export interface CalculateResponse {
  success: boolean;
  available: boolean;
  reason?: string;
  options: DeliveryOption[];
  meta: {
    free_delivery_threshold: number | null;
    remaining_for_free: number | null;
  };
  request_id: string;
}

export type PickupPointType = 'pvz' | 'postamat';

export interface PickupPoint {
  id: string;
  provider: string;
  name: string;
  address: string;
  work_time: string;
  phone?: string;
  lat: number;
  lng: number;
  is_cash: boolean;
  is_card: boolean;
  type?: PickupPointType;
}

export type PvzProviderFilter = 'all' | 'cdek' | 'yandex';
export type PvzTypeFilter = 'all' | 'pvz' | 'postamat';
export type TariffProviderFilter = 'all' | 'cdek' | 'yandex';

export interface CartItem {
  sku: string;
  quantity: number;
  price: number;
  weight: number;
  length?: number;
  width?: number;
  height?: number;
}

export interface Dimensions {
  length: number;
  width: number;
  height: number;
}

export interface CartData {
  items: CartItem[];
  totalPrice: number;
  totalWeight: number;
  dimensions?: Dimensions;
}

export interface WidgetConfig {
  apiUrl: string;
  debug?: boolean;
  onSelect?: (option: DeliveryOption, point?: PickupPoint) => void;
}

export interface WidgetState {
  city: City | null;
  options: DeliveryOption[];
  selectedOption: DeliveryOption | null;
  selectedPickupPoint: PickupPoint | null;
  isLoading: boolean;
  error: string | null;
  showPickupMap: boolean;
  activeCategory: DeliveryCategory | null;
  courierAddress: CourierAddress | null;
}

export interface CategoryConfig {
  id: DeliveryCategory;
  label: string;
  subtitle: string;
  icon: string;
  enabled: boolean;
  disabled?: boolean;
}

export const PROVIDER_LOGOS: Record<DeliveryProvider, string> = {
  cdek: 'https://bitrix.greenwax.ru/upload/iblock/ba7/wj14vs5cd522zq056tuu0q08ftxpapca/Delivery%20company%20(7).svg',
  yandex: 'https://bitrix.greenwax.ru/upload/iblock/d96/by3o7bg1gi9p3sbdnpbxy36zjaafjog1/Delivery%20company%20(6).svg',
  greenwax: 'https://cdn.greenwax.ru/delivery/logos/greenwax.svg',
  boxberry: 'https://cdn.greenwax.ru/delivery/logos/boxberry.svg',
  post: 'https://bitrix.greenwax.ru/upload/iblock/420/y78lp4u7up5rvb7k5sjmeptgbo7exdui/Delivery%20company%20(9).svg',
};

export const MOSCOW_CDEK_ID = 44;

// ============================================
// ORDER PAYLOAD - full delivery data at checkout
// ============================================
export interface DeliveryOrderPayload {
  // Tilda order data
  orderId?: string;          // Tilda order ID if available
  cartTotal: number;         // Cart subtotal (products only)
  cartItems: CartItem[];     // Products

  // Delivery details
  city: { id: number; name: string; cdekId?: number };
  selectedOption: {
    provider: string;        // cdek, yandex, greenwax
    tariffCode: string;
    tariffName: string;
    type: string;            // pickup, courier, express
    price: number;
    deliveryDays: { min: number; max: number };
  };

  // Pickup point (if pickup type)
  pickupPoint?: {
    id: string;
    name: string;
    address: string;
    provider: string;
  };

  // Courier address (if courier type)
  courierAddress?: {
    street: string;
    house: string;
    apartment?: string;
    floor?: string;
    comment?: string;
    fullAddress: string;
  };

  // Customer contacts (from Tilda form)
  customer?: {
    name?: string;
    phone?: string;
    email?: string;
  };

  timestamp: string;
}

// Order creation response
export interface DeliveryOrderResponse {
  orderId: string;
  success: boolean;
  error?: string;
}

// localStorage keys for order buffer
export const LS_ORDER_BUFFER_KEY = 'gw_delivery_order_buffer';
export const LS_PENDING_ORDERS_KEY = 'gw_delivery_pending_orders';
